/* Primal State global behaviours: reveal-on-scroll, count-up stats, snap sliders */
(function () {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Reveal ---------------- */
  const revealIO = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          revealIO.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  function initReveal(root) {
    root.querySelectorAll('.ps-reveal:not(.is-in), .ps-stagger:not(.is-in)').forEach((el) => {
      if (reduce) {
        el.classList.add('is-in');
        return;
      }
      revealIO.observe(el);
    });
  }

  /* ---------------- Count-up ---------------- */
  const countIO = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        countIO.unobserve(e.target);
        runCount(e.target);
      });
    },
    { threshold: 0.4 }
  );

  function runCount(el) {
    const target = parseFloat(el.dataset.psCount || '0');
    const suffix = el.dataset.psSuffix || '';
    const decimals = (String(el.dataset.psCount).split('.')[1] || '').length;
    const dur = reduce ? 0 : parseInt(el.dataset.psDuration || '1600', 10);
    const ring = el.closest('[data-ps-ring]');
    if (ring) ring.style.setProperty('--p', target);
    if (dur === 0) {
      el.textContent = target.toFixed(decimals) + suffix;
      return;
    }
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4);
    function tick(now) {
      const p = Math.min(1, (now - start) / dur);
      el.textContent = (target * ease(p)).toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function initCounters(root) {
    root.querySelectorAll('[data-ps-count]:not([data-ps-counted])').forEach((el) => {
      el.setAttribute('data-ps-counted', '1');
      countIO.observe(el);
    });
  }

  /* ---------------- Sliders ---------------- */
  function initSliders(root) {
    root.querySelectorAll('[data-ps-slider]:not([data-ps-slider-ready])').forEach((slider) => {
      slider.setAttribute('data-ps-slider-ready', '1');
      const track = slider.querySelector('.ps-track');
      if (!track) return;
      const prev = slider.querySelector('[data-ps-prev]');
      const next = slider.querySelector('[data-ps-next]');
      const dots = slider.querySelector('[data-ps-dots]');
      const items = Array.from(track.children);

      const step = () => {
        const first = track.children[0];
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        if (dots) {
          const idx = Math.round(track.scrollLeft / step());
          dots.querySelectorAll('button').forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
        }
        slider.dispatchEvent(new CustomEvent('ps:slide', { detail: { index: Math.round(track.scrollLeft / step()) } }));
      };

      if (dots && items.length > 1) {
        dots.innerHTML = '';
        items.forEach((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: 'smooth' }));
          dots.appendChild(b);
        });
      }

      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      /* pointer drag on desktop */
      let down = false,
        startX = 0,
        startLeft = 0,
        moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) {
          moved = true;
          track.classList.add('ps-track--dragging');
        }
        track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        track.classList.remove('ps-track--dragging');
        if (moved) {
          const i = Math.round(track.scrollLeft / step());
          track.scrollTo({ left: i * step(), behavior: 'smooth' });
        }
      });
      track.addEventListener('click', (e) => { if (moved) e.preventDefault(); }, true);

      slider.psGoTo = (i) => track.scrollTo({ left: i * step(), behavior: 'smooth' });
      update();
    });
  }

  /* ---------------- Cart drawer helpers (fallbacks for Horizon's drawer) ---------------- */
  function cartDrawer() { return document.getElementById('cart-drawer'); }
  function psDrawer() { return document.querySelector('ps-cart-drawer'); }
  function openCartDrawer() {
    const pc = psDrawer(); if (pc && typeof pc.open === 'function') { pc.open(); return; }
    const d = cartDrawer();
    if (!d) { window.location.href = '/cart'; return; }
    const doOpen = () => {
      if (typeof d.open === 'function') { d.open(); return; }
      const dlg = d.querySelector('dialog');
      if (dlg && !dlg.open) { try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); } d.setAttribute('open', ''); }
    };
    if (customElements.get('theme-drawer')) doOpen();
    else {
      let done = false;
      customElements.whenDefined('theme-drawer').then(() => { if (!done) { done = true; doOpen(); } });
      setTimeout(() => { if (!done) { done = true; doOpen(); } }, 800);
    }
  }
  function toggleCartDrawer() {
    const pc = psDrawer(); if (pc && typeof pc.toggle === 'function') { pc.toggle(); return; }
    const d = cartDrawer();
    if (d && typeof d.toggle === 'function') d.toggle();
    else if (d && d.hasAttribute('open') && typeof d.close === 'function') d.close();
    else openCartDrawer();
  }
  /* auto-open after any add-to-cart, even if Horizon's own listener misses it */
  document.addEventListener('shopify:cart:lines-update', (e) => {
    if (psDrawer()) return; /* custom drawer handles it */
    const detail = e.detail || {};
    if (detail.action !== 'add' && (!e.action || e.action !== 'add')) return;
    const d = cartDrawer();
    if (!d) return;
    const finish = () => { if (!d.hasAttribute('open')) openCartDrawer(); };
    if (e.promise && typeof e.promise.then === 'function') e.promise.then(finish).catch(() => {}); else setTimeout(finish, 300);
  });
  window.PS = Object.assign(window.PS || {}, { openCartDrawer, toggleCartDrawer });

  /* ---------------- Boot ---------------- */
  function boot(root) {
    root = root || document;
    initReveal(root);
    initCounters(root);
    initSliders(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const s = e.target.closest('.shopify-section');
    if (s) s.querySelectorAll('.ps-reveal, .ps-stagger').forEach((el) => el.classList.add('is-in'));
  });

  window.PS = Object.assign(window.PS || {}, { boot, initSliders, initReveal, openCartDrawer, toggleCartDrawer });
})();


/* ---------------------------------------------------------
   In-page anchor scrolling
   Capture phase so it fires before Horizon's link interception.
   Uses scrollIntoView because window.scrollTo is a no-op on
   desktop where the scrolling container is not the window.
   Offset comes from scroll-margin-top on the target element,
   with a JS fallback if the target has none.
--------------------------------------------------------- */
(function () {
  'use strict';

  const HEADER_OFFSET = 140;

  function goTo(hash) {
    let el;
    try {
      el = document.querySelector(hash);
    } catch (err) {
      return false;
    }
    if (!el) return false;

    /* if the target has no scroll-margin-top in CSS, apply the offset inline */
    const existing = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (existing === 0) el.style.scrollMarginTop = HEADER_OFFSET + 'px';

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', hash);
    return true;
  }

  document.addEventListener(
    'click',
    function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target.closest('a[href*="#"]');
      if (!a) return;
      if (a.target && a.target !== '_self') return;

      const raw = a.getAttribute('href') || '';
      const hashIndex = raw.indexOf('#');
      if (hashIndex === -1) return;

      const hash = raw.slice(hashIndex);
      if (hash.length < 2) return;

      /* only handle links that stay on the current page */
      const path = raw.slice(0, hashIndex);
      if (path && path !== window.location.pathname && path !== window.location.href) return;

      if (!goTo(hash)) return;

      e.preventDefault();
      e.stopPropagation();
    },
    true /* capture phase */
  );

  /* handle arriving on the page with a hash already in the URL */
  window.addEventListener('load', function () {
    if (window.location.hash.length > 1) {
      setTimeout(() => goTo(window.location.hash), 150);
    }
  });
})();